import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import { formatDate, formatNumber } from "../utils/format";
import ErrorModal from "../components/ErrorModal";
import InvoicePdfImportPanel from "../components/InvoicePdfImportPanel";
import { usePermission } from "../hooks/usePermission";

export default function PurchaseList({ user }) {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("purchases.create");
  const canEdit = hasPermission("purchases.edit");
  const canDelete = hasPermission("purchases.delete");
  const [invoices, setInvoices] = useState([]);
  const [unauthorized, setUnauthorized] = useState(false);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("invoice_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [showPdfImportPanel, setShowPdfImportPanel] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    fetchInvoices();
  }, [user, search, sortField, sortOrder]);

  const fetchInvoices = () => {
    setLoadingInvoices(true);
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    params.append("sort", sortField);
    params.append("order", sortOrder);
    params.append("limit", "1000");  // Fetch up to 1000 invoices

    apiFetch(`/invoices?${params.toString()}`)
      .then((data) => {
        // Handle new paginated response format
        if (data.items) {
          setInvoices(data.items);
        } else {
          setInvoices(data);
        }
      })
      .catch((err) => {
        console.error("Fout bij ophalen facturen:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoadingInvoices(false));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleAddInvoice = () => {
    navigate("/purchases/new");
  };

  const handlePdfParsed = (result) => {
    setShowPdfImportPanel(false);
    navigate("/purchases/new", { state: { initialData: result } });
  };

  const handleViewInvoice = (invoiceId) => {
    navigate(`/purchases/${invoiceId}`);
  };

  const handleDelete = (invoice) => {
    setDeletingInvoice(invoice);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!deletingInvoice) return;

    setLoading(true);
    apiFetch(`/invoices/${deletingInvoice.id}`, { method: "DELETE" })
      .then(() => {
        setShowDeleteModal(false);
        setDeletingInvoice(null);
        fetchInvoices();
      })
      .catch((err) => {
        console.error("Fout bij verwijderen factuur:", err);
        setErrorModal({ show: true, message: "Er is een fout opgetreden bij het verwijderen van de factuur." });
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoading(false));
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "↕";
    return sortOrder === "asc" ? "↑" : "↓";
  };

  if (unauthorized) return <Navigate to="/unauthorized" replace />;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Aankopen</h1>
        {canCreate && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowPdfImportPanel(true)}
              className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50"
            >
              Importeer factuur (PDF)
            </button>
            <button
              onClick={handleAddInvoice}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              + Nieuwe Factuur
            </button>
          </div>
        )}
      </div>

      {showPdfImportPanel && (
        <InvoicePdfImportPanel
          onClose={() => setShowPdfImportPanel(false)}
          onParsed={handlePdfParsed}
        />
      )}

      {/* Search Bar */}
      <div className="mb-4 flex gap-2">
        <input
          type="text"
          placeholder="Zoek op leverancier..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md px-4 py-2 border rounded"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="px-4 py-2 border rounded hover:bg-gray-100 text-gray-700"
          >
            Wissen
          </button>
        )}
      </div>

      {/* Loading Indicator */}
      {loadingInvoices && (
        <div className="flex justify-center items-center py-12 bg-white shadow rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Facturen laden...</span>
        </div>
      )}

      {/* Invoices Table */}
      {!loadingInvoices && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort("invoice_date")}
                >
                  Factuurdatum {getSortIcon("invoice_date")}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort("supplier")}
                >
                  Leverancier {getSortIcon("supplier")}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Aantal producten
                </th>
                <th
                  className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort("total_amount")}
                >
                  Totaalbedrag {getSortIcon("total_amount")}
                </th>
                {(canEdit || canDelete) && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Acties
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.length === 0 ? (
              <tr>
                <td colSpan={canEdit || canDelete ? "5" : "4"} className="px-6 py-4 text-center text-gray-500">
                  Geen facturen gevonden
                </td>
              </tr>
            ) : (
              invoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleViewInvoice(invoice.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                    {formatDate(invoice.invoice_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                    {invoice.supplier_name || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-gray-600">
                    {invoice.item_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900">
                    €{formatNumber(invoice.total_amount)}
                  </td>
                  {(canEdit || canDelete) && (
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      {canDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(invoice);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          Verwijderen
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Factuur Verwijderen</h2>
            <p className="mb-6 text-gray-700">
              Weet je zeker dat je de factuur van <strong>{deletingInvoice?.supplier_name}</strong> op{" "}
              <strong>{deletingInvoice && formatDate(deletingInvoice.invoice_date)}</strong> wilt verwijderen?
              <br />
              Deze actie kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
                disabled={loading}
              >
                Annuleren
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? "Bezig..." : "Verwijderen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <ErrorModal
        show={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: "" })}
      />
    </div>
  );
}
